import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation, useQueries, useQueryClient } from '@tanstack/react-query';
import { useWatch } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { getGeocode, getLatLng } from 'use-places-autocomplete';
import * as Yup from 'yup';
import { getRolePermission } from '@/api/roles-and-permission';
import { postImportProjects } from '@/api/Sherpa/Project';
import type { IPostProjectPayload } from '@/api/Sherpa/Project/modules/ProjectCore/types';
import { projectLeadRequiredPermissions } from '@/components/Sherpa/Projects/NewProjectForm/BasicProjectInfo/constants';
import Button from '@/components/UI/Button';
import { HookForm } from '@/components/UI/HookForm/Form';
import { HookFileInput } from '@/components/UI/HookForm/Form/File';
import { HookReactSingleSelect } from '@/components/UI/HookForm/Form/ReactSelect';
import { HookFormProvider } from '@/components/UI/HookForm/Provider';
import type { ValidationFunction } from '@/components/UI/HookForm/types';
import { useProjectTemplates } from '@/hooks/ReactQueryHooks/useProjectTemplateQuery';
import { useProjectSelectorUsers } from '@/modules/sherpa/hooks/useProjectSelectorUsers';
import { refreshProjectsListQueries } from '@/features/projects/query-cache';
import { loadProjectTemplatesInBatches } from '@/features/projects/template-load';
import { workspaceSelector } from '@/store';
import { notifyUser } from '@/store/Toast';
import type { User } from '@/types/User';
import { compareTwoArrays, sanitizeResponseError } from '@/utils';

const validationSchema = Yup.object({
  file: Yup.mixed().required('Required'),
  projectTemplate: Yup.object({
    label: Yup.string(),
    value: Yup.string(),
  }).test('projectTemplate', 'Required', (val) => {
    return val.label !== undefined && val.value !== undefined;
  }),
});

const ImportProjectForm = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const { currentWorkspace } = useSelector(workspaceSelector);
  const fetchProjectTemplates = useProjectTemplates({});
  const projectTemplates = fetchProjectTemplates?.data?.data;
  const projectTemplateValues = useWatch({ name: 'projectTemplate' });
  const projectLeadValues = useWatch({ name: 'projectLead' });

  const fetchUsers = useProjectSelectorUsers();
  const users = fetchUsers.isError ? undefined : fetchUsers.data;

  //Only users who have all project permission can become a project lead
  const authorizedUsers = useQueries({
    queries: users
      ? users?.map((user: User) => {
          return {
            queryKey: ['user', user.id],
            queryFn: () =>
              getRolePermission(String(user.roleId)).then((permissions: any) => {
                const projectPermissions = permissions.Project.filter((pp: any) => pp.allow)
                  .map((p: any) => p.slug)
                  .filter((ps: string) => ps !== 'LIST_ALL_PROJECTS');

                const { array2Diff } = compareTwoArrays(projectPermissions, projectLeadRequiredPermissions);

                return {
                  label: `${user.name} <${user.email}>`,
                  value: user.id.toString(),
                  authorized: array2Diff.length === 0,
                };
              }),
          };
        })
      : [],
  });

  const isLoadingUsers = fetchUsers.isLoading || authorizedUsers.some((query) => query.isLoading);
  const hasUserError = fetchUsers.isError || authorizedUsers.some((query) => query.isError);
  const usersUnavailable = isLoadingUsers || hasUserError;
  const retryUsers = () => {
    if (fetchUsers.isError) void fetchUsers.refetch();
    for (const query of authorizedUsers) {
      if (query.isError) void query.refetch();
    }
  };

  const validProjectLeadOptions = (usersUnavailable ? [] : authorizedUsers)
    .filter((au: any) => au.data?.authorized)
    .map((u: any) => ({
      label: u.data?.label,
      value: u.data?.value,
    }));

  const importProjectsData = useMutation(
    async (payload: { data: { projects: IPostProjectPayload[] }; projectTemplateId?: number }) => {
      const response = await postImportProjects({ data: payload.data });
      const projectIds = response.data.projectsId as number[];
      const templateLoadFailures = payload.projectTemplateId
        ? await loadProjectTemplatesInBatches(projectIds, payload.projectTemplateId)
        : [];

      return { projectIds, templateLoadFailures };
    },
    {
      onSuccess: async ({ projectIds, templateLoadFailures }) => {
        await refreshProjectsListQueries(queryClient);
        navigate('/app/sherpa/projects');
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: templateLoadFailures.length
              ? `${projectIds.length - templateLoadFailures.length} projects imported with templates. ${templateLoadFailures.length} template load${
                  templateLoadFailures.length === 1 ? '' : 's'
                } failed.`
              : 'Projects import request sent successfully.',
            toastType: templateLoadFailures.length ? 'failed' : 'success',
          })
        );
      },
      onError: (error: any) => {
        const resError = sanitizeResponseError(error);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: resError,
            toastType: 'failed',
          })
        );
      },
    }
  );

  const handleSubmit = async (values: { file: File[] }) => {
    if (usersUnavailable) return;
    const projectLeadId: number | undefined = Number(projectLeadValues?.value);
    const workspaceId: number | undefined = currentWorkspace?.id;

    if (!projectLeadId) {
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: 'Unable to find Project Lead',
          toastType: 'failed',
        })
      );

      return;
    }
    if (!workspaceId) {
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: 'Please select a program you would like to import.',
          toastType: 'failed',
        })
      );

      return;
    }

    try {
      if (values.file[0]) {
        const fileReader = new FileReader();
        fileReader.readAsText(values.file[0], 'UTF-8');
        fileReader.onload = async (e) => {
          const data = JSON.parse(e.target?.result as string)?.data;

          if (data.length > 100) {
            dispatch(
              notifyUser({
                showToast: true,
                toastMessage: 'Only 100 applications can be imported per file',
                toastType: 'failed',
              })
            );
          } else {
            const formattedData = data.map((d: any) => {
              const address = d.Street + d.City + d.State + d.Zip;

              return getGeocode({ address })
                .then((res: any) => {
                  const { lat, lng } = getLatLng(res[0]);
                  const placeId = res[0].place_id;
                  return {
                    name: d['Applicant Name'],
                    address: d.Street,
                    city: d.City,
                    state: 'Alabama',
                    zipCode: d.Zip,
                    projectType: 'Single Family',
                    constructionType: 'Renovation',
                    neighborhood: d.Neighborhood,
                    units: null,
                    squareFeet: 1500,
                    status: 'OUT_TO_RFP',
                    goals: [],
                    budget: [
                      {
                        name: 'CDBG Funds',
                        amount: 30000,
                      },
                    ],
                    uses: [],
                    location: {
                      address1: d.Street,
                      city: d.City,
                      state: d.State,
                      postalCode: d.Zip,
                      country: 'United States of America',
                      placeId: placeId,
                      lat: lat,
                      lng: lng,
                    },
                    sources: [
                      {
                        name: 'CDBG Funds',
                        amount: 30000,
                      },
                    ],
                    stage: 'PRE_DEVELOPMENT',
                    subsidies: [],
                    archived: false,
                    projectLeadId: projectLeadId,
                    workspaceId: workspaceId,
                  };
                })
                .catch((_error: any) => {
                  dispatch(
                    notifyUser({
                      showToast: true,
                      toastMessage: `Unable to resolve entered address for ${d.Street}`,
                      toastType: 'failed',
                    })
                  );
                  return {
                    name: d['Applicant Name'],
                    address: d.Street,
                    city: d.City,
                    state: 'Alabama',
                    zipCode: d.Zip,
                    projectType: 'Single Family',
                    constructionType: 'Renovation',
                    neighborhood: d.Neighborhood,
                    units: null,
                    squareFeet: 1500,
                    status: 'OUT_TO_RFP',
                    goals: [],
                    budget: [
                      {
                        name: 'CDBG Funds',
                        amount: 30000,
                      },
                    ],
                    uses: [],
                    location: {
                      address1: d.Street,
                      city: d.City,
                      state: d.State,
                      postalCode: d.Zip,
                      country: 'United States of America',
                      placeId: null,
                      lat: null,
                      lng: null,
                    },
                    sources: [
                      {
                        name: 'CDBG Funds',
                        amount: 30000,
                      },
                    ],
                    stage: 'PRE_DEVELOPMENT',
                    subsidies: [],
                    projectLeadId: projectLeadId,
                    workspaceId: workspaceId,
                  };
                });
            });

            const resolvedData: IPostProjectPayload[] = await Promise.all(formattedData);
            importProjectsData.mutate({
              data: { projects: resolvedData },
              projectTemplateId: Number(projectTemplateValues.value),
            });
          }
        };
      }
    } catch (err) {
      console.log(err);
    }
  };
  const validateJson: ValidationFunction = (value: any) => {
    if (value) {
      const file = (value as File[])[0];
      const extension = file.name.split('.').pop();
      if (extension !== 'json') {
        return 'JSON File is required.';
      }
      return undefined;
    }
    return undefined;
  };
  return (
    <HookForm onSubmit={handleSubmit} className='p-6 space-y-6'>
      <HookFileInput name='file' accept='.json' label='File' validate={validateJson} />

      <HookReactSingleSelect
        classNames={{
          control: ({ isDisabled }) => (isDisabled ? '!cursor-not-allowed' : '!cursor-pointer'),
          option: ({ isDisabled }) => (isDisabled ? '!cursor-not-allowed' : '!cursor-pointer'),
        }}
        componentClassName='col-span-6 sm:col-span-3 lg:col-span-2'
        label='Project Templates'
        id='project-template'
        name='projectTemplate'
        options={projectTemplates?.map((pt: any) => ({
          label: pt.name,
          value: pt.id,
        }))}
      />

      <HookReactSingleSelect
        classNames={{
          control: ({ isDisabled }) => (isDisabled ? '!cursor-not-allowed' : '!cursor-pointer'),
          option: ({ isDisabled }) => (isDisabled ? '!cursor-not-allowed' : '!cursor-pointer'),
        }}
        componentClassName='col-span-6 sm:col-span-3 lg:col-span-2'
        label='Project Lead'
        id='project-lead'
        isDisabled={usersUnavailable}
        name='projectLead'
        options={validProjectLeadOptions}
      />

      {isLoadingUsers && <p role='status'>Loading available project leads...</p>}
      {hasUserError && (
        <div role='alert'>
          Unable to load available project leads.
          <button
            type='button'
            className='cursor-pointer disabled:cursor-not-allowed'
            disabled={fetchUsers.isFetching || authorizedUsers.some((query) => query.isFetching)}
            onClick={retryUsers}
          >
            Retry
          </button>
        </div>
      )}
      <div className='flex gap-4 pt-5 justify-end'>
        <Button label='Cancel' btnStyle='neutral' />
        <Button
          label={importProjectsData.isLoading ? 'Importing...' : 'Import'}
          btnStyle='primary'
          type='submit'
          disabled={importProjectsData.isLoading || usersUnavailable}
        />
      </div>
    </HookForm>
  );
};

export const ImportProjects = () => {
  return (
    <div>
      <h3 className='mb-4'>Import Projects</h3>
      <HookFormProvider mode='all' resolver={yupResolver(validationSchema)}>
        <ImportProjectForm />
      </HookFormProvider>
    </div>
  );
};
